import { useQuery } from "@apollo/client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import { Form, Formik } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import { createOrEditSubaccountInputSchema } from "@stw/yup-validators";

import {
  CreateSubaccountMutationVariables,
  GetLatestBudgetsQuery,
  GetLatestBudgetsQueryVariables,
} from "~frontend/__generated__/graphql";
import { InternalLink } from "~frontend/components/Link";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { FormikDurationField } from "~frontend/components/forms/FormikDurationField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import FormikTextingHoursFields from "~frontend/components/forms/FormikTextingHoursFields";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { GET_LATEST_BUDGETS } from "~frontend/graphql/budgets";
import { useErrorMessageFormatter } from "~frontend/util/errors";

function BudgetWarning(): ReactElement | null {
  const { loading, error, data } = useQuery<
    GetLatestBudgetsQuery,
    GetLatestBudgetsQueryVariables
  >(GET_LATEST_BUDGETS);

  if (loading || error) {
    return null;
  }

  const budgets = data?.getLatestBudgets || [];
  const activeBudget = budgets.find(b => b.budget?.endDate === null);

  if (!activeBudget) {
    return null;
  }

  return (
    <Alert severity="warning" sx={{ mb: 2, mx: 3 }}>
      <strong>Note:</strong> Once created, this subaccount will have budgeting
      turned off by default. Once created, you can turn budgeting on for this
      subaccount on the{" "}
      <InternalLink to="/organization/budgets" target="_blank">
        Budgets Page
      </InternalLink>
      .
    </Alert>
  );
}

export default function AddSubaccountDialog({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateSubaccountMutationVariables) => Promise<void>;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();

  return (
    <Dialog open={open}>
      <DialogTitle>New Subaccount</DialogTitle>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          name: "",
          slug: "",
          textingHoursTimezone: "America/New_York",
          textingHoursStart: "08:00:00",
          textingHoursEnd: "21:00:00",
          texterAssignmentBatchSize: 25,
          texterAssignmentExpirationMinutes: 60,
        }}
        validationSchema={createOrEditSubaccountInputSchema(Yup).fields.input}
        onSubmit={async (values, formik) => {
          try {
            await onSubmit({
              subaccountInput: {
                name: values.name,
                slug: values.slug,
                textingHoursTimezone: values.textingHoursTimezone,
                textingHoursStart: values.textingHoursStart,
                textingHoursEnd: values.textingHoursEnd,
                texterAssignmentBatchSize: values.texterAssignmentBatchSize,
                texterAssignmentExpirationMinutes:
                  values.texterAssignmentExpirationMinutes,
              },
            });
            formik.resetForm();
          } catch (e) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(e),
            });
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent sx={{ minWidth: "400px" }}>
              <FormikErrorBanner />
              <FormikTextField
                name="name"
                label="Name"
                fullWidth
                inputProps={{ "data-testid": "add-subaccount-name-field" }}
              />
              <FormikTextField name="slug" label="Slug" fullWidth />
              <FormikTextingHoursFields label="Texting Hours" />

              <FormikTextField
                name="texterAssignmentBatchSize"
                label="Texter Assignment Batch Size"
                type="number"
                bottomLabel="The number of replies that will be assigned to a texter at a time"
                fullWidth
              />
              <FormikDurationField
                name="texterAssignmentExpirationMinutes"
                label="Assignment Duration"
                bottomLabel="After this amount of time, assignments will go back into the pool for reassignment"
                fullWidth
              />
            </DialogContent>
            <BudgetWarning />

            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText="Add"
                loadingText="Adding..."
                data-testid="add-subaccount-button"
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
